import React from 'react';
import { User, Phone, Mail, AtSign, Building2, Search, ShoppingCart } from 'lucide-react';
import { Button } from './Button';
import { VerticalTabs, type VerticalTab } from './VerticalTabs';
import type { CustomerDocType } from '../../db/schemas';

interface CustomerSidebarBaseProps {
  customer?: CustomerDocType | null;
  /** Branch/sucursal name to display */
  branchName?: string;
}

interface CompactVariantProps extends CustomerSidebarBaseProps {
  variant: 'compact';
  /** Called when user wants to search/change client */
  onSearchClient?: () => void;
  /** Called when user wants to remove the associated client */
  onRemoveClient?: () => void;
  /** Optional extra cards rendered below the client card */
  contextCards?: React.ReactNode;
}

interface ProfileVariantProps extends CustomerSidebarBaseProps {
  variant: 'profile';
  /** Vertical tab configuration */
  tabs: VerticalTab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  tabSectionAfter?: string[];
  tabSectionHeaders?: Record<string, string>;
  /** Called when user clicks "Nueva Venta" */
  onNewSale?: () => void;
}

export type CustomerSidebarProps = CompactVariantProps | ProfileVariantProps;

export function CustomerSidebar(props: CustomerSidebarProps) {
  const { customer, variant, branchName } = props as any;
  const targetCustomer = customer || null;

  const cleanPhone = targetCustomer?.phone?.replace(/\D/g, '');

  return (
    <>
      {/* ── Client Profile Card ── */}
      <div className="bg-surface-secondary backdrop-blur-md border border-border p-4 lg:p-5 rounded-2xl shadow-xs transition-all">
        {targetCustomer ? (
          <div>
            <div className="flex items-center gap-3 lg:block">
              <div className="w-10 h-10 lg:w-14 lg:h-14 bg-brand-orange/10 rounded-full flex items-center justify-center lg:mb-4 border border-brand-orange/20 shrink-0">
                <User className="w-5 h-5 lg:w-7 lg:h-7 text-brand-orange" />
              </div>

              <div className="min-w-0 flex-1">
                {variant === 'compact' && (
                  <span className="text-[10px] font-extrabold uppercase tracking-wider text-brand-orange bg-brand-orange/10 px-2 py-0.5 rounded border border-brand-orange/20 inline-block">
                    Cliente Asociado
                  </span>
                )}

                <h4 className="font-extrabold text-sm lg:text-base text-text-primary truncate">{targetCustomer.name}</h4>

                {targetCustomer.instagram && (
                  <div className="flex items-center gap-1.5 text-brand-sky font-semibold text-xs mt-0.5">
                    <AtSign className="w-3.5 h-3.5" /> {targetCustomer.instagram}
                  </div>
                )}
              </div>
            </div>

            {/* Contact details */}
            <div className="mt-2.5 lg:mt-4 space-y-1.5 lg:space-y-2.5 pt-2.5 border-t border-border text-xs text-text-secondary">
              {targetCustomer.phone && (
                <div className="flex items-center gap-2 min-w-0">
                  <Phone className="w-3.5 h-3.5 text-text-tertiary shrink-0" />
                  <span className="truncate">{targetCustomer.phone}</span>
                </div>
              )}
              {targetCustomer.email && (
                <div className="flex items-center gap-2 min-w-0">
                  <Mail className="w-3.5 h-3.5 text-text-tertiary shrink-0" />
                  <span className="truncate">{targetCustomer.email}</span>
                </div>
              )}
              {branchName && (
                <div className="flex items-center gap-2 min-w-0">
                  <Building2 className="w-3.5 h-3.5 text-text-tertiary shrink-0" />
                  <span className="truncate">{branchName}</span>
                </div>
              )}
            </div>

            {/* Actions — variant-specific */}
            {variant === 'compact' && (
              <div className="mt-3 lg:mt-4 pt-3 border-t border-border flex items-center gap-2">
                <Button 
                  data-testid="associate-customer-btn"
                  variant="outline" 
                  size="xs" 
                  icon={Search} 
                  fullWidth 
                  onClick={props.onSearchClient}
                  className="hover:border-brand-orange hover:text-brand-orange"
                >
                  Cambiar
                </Button>
                <Button 
                  variant="ghost" 
                  size="xs" 
                  onClick={props.onRemoveClient} 
                  className="hover:bg-error/10 hover:text-error text-text-tertiary"
                >
                  Quitar
                </Button>
              </div>
            )}

            {variant === 'profile' && (
              <div className="mt-3 lg:mt-4 pt-3 border-t border-border flex items-center lg:flex-col gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  icon={ShoppingCart}
                  fullWidth
                  onClick={props.onNewSale}
                >
                  Nueva Venta
                </Button>
                {cleanPhone && (
                  <Button
                    as="a"
                    href={`https://wa.me/${cleanPhone}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="success"
                    size="sm"
                    icon={Phone}
                    fullWidth
                  >
                    WhatsApp
                  </Button>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Anonymous / Walk-in state */
          <div>
            <div className="w-10 h-10 lg:w-14 lg:h-14 bg-surface-tertiary rounded-full flex items-center justify-center mb-2 lg:mb-4 border border-border">
              <User className="w-5 h-5 lg:w-7 lg:h-7 text-text-tertiary" />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-text-tertiary bg-surface-tertiary px-2 py-0.5 rounded border border-border">
              Venta Mostrador
            </span>
            <h4 className="font-extrabold text-sm lg:text-base text-text-primary mt-2">Cliente Anónimo (Walk-in)</h4>
            
            {variant === 'compact' && (
              <div className="mt-3 pt-3 border-t border-border">
                <Button 
                  data-testid="associate-customer-btn"
                  variant="outline" 
                  size="sm" 
                  icon={Search} 
                  fullWidth 
                  onClick={props.onSearchClient}
                  className="hover:bg-brand-orange/10 hover:text-brand-orange hover:border-brand-orange/30 font-bold"
                >
                  Buscar / Registrar Cliente
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Variant-specific additional content ── */}

      {variant === 'compact' && props.contextCards}

      {variant === 'profile' && (
        <div className="bg-surface-secondary backdrop-blur-md border border-border rounded-2xl shadow-xs p-2">
          <VerticalTabs
            tabs={props.tabs}
            activeTab={props.activeTab}
            onTabChange={props.onTabChange}
            sectionAfter={props.tabSectionAfter}
            sectionHeaders={props.tabSectionHeaders}
          />
        </div>
      )}
    </>
  );
}
